import { Validator } from '../validation/validator.util';
import { ValidatorInput } from '../validation/validation-descriptor.entity';
import { ValidationError } from '../validation/validation.error';
import { ValidationChecks } from '../validation/validation-checks.enum';
import { AppConstants } from '../../app-constants.service';

import { UtilFunctions } from '../../pipes.module/_util.functions';
import { VertragData } from '../../../generated/api/kreditportalBackendAPI.schemas';
import { Produktlinie } from '../json/produktlinie.model';
import { KreditlinieStatic } from '../json/kreditlinie-static.model';
import { KreditlinieRuled } from '../json/kreditlinie-ruled.model';
import { ProduktlinieVhvKautionEntry } from '../json/produktlinie-vhv-kaution-entry.model';

export class ErhoeheKreditlinieModel {

    private readonly checkStaticAntrag = (field: ValidatorInput) => {
        field.valid = true;

        if (!this.staticTable && !this.vhvKautionTable) {
            return;
        }

        if (field.value.length === 0) {
            field.valid = false;
        }
    };

    private readonly checkRuledAntrag = (field: ValidatorInput) => {
        field.valid = true;

        if (!this.ruledData) {
            return;
        }

        if (!Validator.isCustomRegexOk(field)) {
            field.valid = false;
            field.errorMsg = 'Bitte geben Sie eine Zahl ein.';
        }

        const produktlinie: Produktlinie = this.ruledData.getCurrentProduktlinie(this.produktlinieAuswahl);
        if (this.getGesamthoeheRuled() < produktlinie.start) {
            field.valid = false;
            field.errorMsg = 'Das Verringern der Linie ist nicht vorgesehen.';
        }

        if (produktlinie.next === 0 && this.getGesamthoeheRuled() != produktlinie.start && this.getGesamthoeheRuled() != produktlinie.maximalHoehe) {
            field.valid = false;
            field.errorMsg = 'Die Eingabe ist nicht zulässig.';
        }

        if (this.getGesamthoeheRuled() != produktlinie.start && (this.getGesamthoeheRuled() % produktlinie.step) > 0) {
            field.valid = false;
            field.errorMsg = 'Die Eingabe ist nicht zulässig.';
        }

        if (produktlinie.maximalHoehe < this.getGesamthoeheRuled()) {
            field.valid = false;
            const maximalHoeheFormatted = produktlinie.maximalHoehe.formatMoney(2, 3, '.', ',');
            field.errorMsg = 'Dieser Betrag führt zu einer Überschreitung der Maximalhöhe von ' + maximalHoeheFormatted + ' €.';
        }

        if (produktlinie.start > this.getGesamthoeheRuled()) {
            field.valid = false;
            field.errorMsg = 'Dieser Betrag führt zu einer Unterschreitung der Minimalhöhe.';
        }

        if (this.getGesamthoeheRuled() < this.currVertrag.rahmenBetrag) {
            field.valid = false;
            field.errorMsg = 'Das Verringern der Linie ist nicht vorgesehen.';
        }

        if (this.currVertrag.modul.indexOf(produktlinie.produktlinie) >= 0 && this.getGesamthoeheRuled() === this.currVertrag.rahmenBetrag) {
            field.valid = false;
            field.errorMsg = 'Die Eingabe ist nicht zulässig.';
        }
    };

    private readonly checkBuergschaftbetrag = (field: ValidatorInput) => {
        field.valid = true;

        if (!this.individual) {
            return;
        }

        if (field.value.length === 0 || !field.regex.test(field.value)) {
            field.valid = false;
            field.errorMsg = 'Bitte geben Sie eine Zahl ein.';

            return;
        }

        const n = this.getGesamthoehe();
        if (!(n === 15000 || n === 25000 || n % 10000 === 0)) {
            field.valid = false;
            field.errorMsg = 'Kreditlinienerhöhungen sind nur auf volle Zehntausend möglich.';
        }
    };


    // Verschiedene Varianten, die an der Oberflaeche moeglich sind
    staticTable: KreditlinieStatic = null;
    ruledData: KreditlinieRuled = null;

    vhvKautionTable: ProduktlinieVhvKautionEntry = null;
    individual = false;
    keineErhoehung = false;

    // Eingabe-Daten

    // Individuelle Erhoehung oder nach Regel erhoeht
    buergschaftbetrag = new ValidatorInput(
        'buergschaftbetrag',
        '',
        'Bürgschaftbetrag',
        /^\d{1,3}(\.\d{3})*$/,
        null,
        false,
        0,
        this.checkBuergschaftbetrag,
    );

    // Erhoehung nach Regel (ruled)
    // RegExp fuer Tausendertrennzeichen und Dezimalwerte: /^\d+(.\d{3})?(,\d{1,2})?$/
    buergschaftslinieEingabe = new ValidatorInput(
        'buergschaftslinieEingabe',
        '',
        'Bürgschaftslinieneingabe',
        /^\d{1,3}(\.\d{3})*$/,
        'Erhöhung nur auf volle Zehntausender möglich.',
        false,
        0,
        this.checkRuledAntrag,
    );

    produktlinie: string;

    produktlinieAuswahl: string;
    vhvKautionNextProduktlinien: string[];

    // Erhoehung nach Buergschaftslinie (static)
    buergschaftslinieAuswahl = new ValidatorInput(
        'buergschaftslinie',
        '',
        'Bürgschaftslinie',
        /^.*$/,
        null,
        false,
        0,
        this.checkStaticAntrag,
    );

    geschaeftsjahr = new ValidatorInput(
        'geschaeftsjahr',
        '',
        'Geschäftsjahr',
        /^.*$/,
        null,
        false,
        0,
        this.checkStaticAntrag,
    );

    bestaetigung = new ValidatorInput(
        'bestaetigung',
        '',
        'Bestätigung',
        /^.*$/,
        null,
        false,
        ValidationChecks.NOT_EMPTY_CHECK | ValidationChecks.CHECK_BOX_SELECTED_CHECK,
    );

    confirmationDate: string;

    currVertrag: VertragData = {};

    readonly years: number[];

    collection: ValidatorInput[] = [
        this.buergschaftbetrag,
        this.buergschaftslinieAuswahl,
        this.geschaeftsjahr,
        this.buergschaftslinieEingabe,
        this.bestaetigung,
    ];

    constructor(private readonly appConstants: AppConstants) {
        this.years = UtilFunctions.calculateNextYears(this.appConstants.thresholdForNextYear, 1);

        if (this.years.length === 1) {
            this.geschaeftsjahr.value = this.years[0];
        }
    }

    getGesamthoehe(): number {
        const eingabe: number = +(this.buergschaftbetrag.value + '')
            .replace(/\./g, '')
            .replace(',', '');

        if (isNaN(eingabe)) {
            return 0;
        }

        return (this.currVertrag.rahmenBetrag + eingabe);
    }

    getDifferenzStatic(): number {
        return (+this.buergschaftslinieAuswahl.value - this.currVertrag.rahmenBetrag);
    }

    getBuergschaftslinieEingabe(): number {
        const eingabe: number = +(this.buergschaftslinieEingabe.value + '')
            .replace(/\./g, '')
            .replace(',', '');

        if (isNaN(eingabe)) {
            return 0;
        }

        return eingabe;
    }

    setBuergschaftslinieEingabe(value: number) {
        this.buergschaftslinieEingabe.value = value;
    }


    getGesamthoeheRuled(): number {
        return (this.currVertrag.rahmenBetrag + this.getBuergschaftslinieEingabe());
    }

    getGesamthoeheStatic(): number {
        return (this.currVertrag.rahmenBetrag + this.getBuergschaftslinieEingabe());
    }

    getBeitrag(): number {
        return this.staticTable.getEntry(this.buergschaftslinieAuswahl.value).jahresBeitrag;
    }

    getBeitragVhvKaution(): number {
        return this.vhvKautionTable.getEntry(this.buergschaftslinieAuswahl.value).jahresBeitrag;
    }

    getBeitragRuled(): number {
        return this.ruledData.getBeitrag(
            this.produktlinieAuswahl,
            (this.getBuergschaftslinieEingabe() + this.currVertrag.rahmenBetrag),
        );
    }

    getDate(): string {
        return this.confirmationDate;
    }

    validateField(field: ValidatorInput) {
        field.valid = Validator.doValidate(field, 1, []);
    }

    validate(untilLabel = 'never stop(dummy)'): ValidationError[] {
        const result: ValidationError[] = [];
        for (const field of this.collection) {
            field.valid = Validator.doValidate(field, 1, result);

            if (field.name === untilLabel) {
                break;
            }
        }

        return result.length === 0 ? null : result;
    }

}
